/**
 * SalonID Parallax Gallery - Enhanced Version
 * Optimized scroll-driven zoom with lazy loading and performance improvements
 */
(function() {
  'use strict';

  class ParallaxGallery {
    constructor(section) {
      this.section = section;
      this.container = section.querySelector('.salonid-parallax__container');
      this.imageWrappers = section.querySelectorAll('.salonid-parallax__image-wrapper');
      this.overlay = section.querySelector('.salonid-parallax__overlay');
      this.images = section.querySelectorAll('.salonid-parallax__image');
      
      // Scale values for each image
      this.scaleTargets = [4, 5, 6, 5, 6, 4];
      
      this.isVisible = false;
      this.ticking = false;
      this.initialized = false;
      this.lastScrollProgress = -1; // Track to avoid unnecessary updates
      
      this.init();
    }

    init() {
      if (!this.container) return;
      
      // Set initial state immediately
      this.setInitialState();
      
      // Setup lazy loading for images
      this.setupLazyLoading();
      
      // Wait for layout to settle
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          this.initialized = true;
          
          // Apply GPU acceleration
          this.setupGPUAcceleration();
          
          // Set up intersection observer
          this.setupIntersectionObserver();
          
          // Bind methods
          this.handleScroll = this.handleScroll.bind(this);
          this.handleResize = this.debounce(this.handleResize.bind(this), 100);
          
          // Initial update
          this.updateParallax();
        });
      });
    }

    setInitialState() {
      // Set initial transform to prevent flash
      this.imageWrappers.forEach((wrapper) => {
        wrapper.style.transform = 'scale(1) translate3d(0, 0, 0)';
        wrapper.style.opacity = '1';
        wrapper.removeAttribute('data-scaled');
      });
      
      if (this.overlay) {
        this.overlay.style.transform = 'scale(0.8) translate3d(0, 0, 0)';
        this.overlay.style.opacity = '0';
      }
    }

    setupLazyLoading() {
      if (!('IntersectionObserver' in window)) {
        // Fallback: load all images immediately
        this.images.forEach(img => this.loadImage(img));
        return;
      }

      const imageObserver = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            this.loadImage(entry.target);
            imageObserver.unobserve(entry.target);
          }
        });
      }, {
        rootMargin: '50px'
      });

      this.images.forEach(img => {
        if (img.complete) {
          this.markImageLoaded(img);
        } else {
          imageObserver.observe(img);
        }
      });
      
      this.imageObserver = imageObserver;
    }

    loadImage(img) {
      if (img.dataset.loaded) return;
      
      const src = img.getAttribute('src');
      if (!src) return;

      img.addEventListener('load', () => {
        this.markImageLoaded(img);
      }, { once: true });

      img.addEventListener('error', () => {
        console.warn('Failed to load image:', src);
        this.markImageLoaded(img);
      }, { once: true });

      // If already loaded
      if (img.complete) {
        this.markImageLoaded(img);
      }
    }

    markImageLoaded(img) {
      img.classList.add('loaded');
      img.dataset.loaded = 'true';
      
      const container = img.closest('.salonid-parallax__image-inner');
      if (container) {
        container.classList.add('image-loaded');
      }
    }

    setupGPUAcceleration() {
      // Apply to all image wrappers
      this.imageWrappers.forEach(wrapper => {
        wrapper.style.willChange = 'transform, opacity';
        wrapper.style.backfaceVisibility = 'hidden';
        wrapper.style.perspective = '1000px';
      });
      
      // Apply to overlay
      if (this.overlay) {
        this.overlay.style.willChange = 'transform, opacity';
        this.overlay.style.backfaceVisibility = 'hidden';
      }
    }

    setupIntersectionObserver() {
      if (!('IntersectionObserver' in window)) {
        // Fallback: always listen
        window.addEventListener('scroll', this.handleScroll, { passive: true });
        window.addEventListener('resize', this.handleResize, { passive: true });
        return;
      }

      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          this.isVisible = entry.isIntersecting;
          
          if (this.isVisible) {
            window.addEventListener('scroll', this.handleScroll, { passive: true });
            window.addEventListener('resize', this.handleResize, { passive: true });
            this.updateParallax();
          } else {
            window.removeEventListener('scroll', this.handleScroll);
            window.removeEventListener('resize', this.handleResize);
          }
        });
      }, {
        rootMargin: '100px 0px'
      });

      observer.observe(this.container);
      this.observer = observer;
    }

    handleScroll() {
      if (!this.initialized) return;
      
      if (!this.ticking) {
        window.requestAnimationFrame(() => {
          this.updateParallax();
          this.ticking = false;
        });
        this.ticking = true;
      }
    }

    handleResize() {
      if (!this.initialized) return;
      this.lastScrollProgress = -1; // Force update on resize
      this.handleScroll();
    }

    updateParallax() {
      if (!this.initialized) return;
      
      const rect = this.container.getBoundingClientRect();
      const containerHeight = this.container.offsetHeight;
      const viewportHeight = window.innerHeight;
      
      // Calculate scroll progress (0 to 1)
      const scrollStart = Math.max(0, -rect.top);
      const scrollRange = containerHeight - viewportHeight;
      
      let scrollProgress = 0;
      if (scrollRange > 0) {
        scrollProgress = Math.max(0, Math.min(1, scrollStart / scrollRange));
      }
      
      // Early exit if not in viewport
      if (rect.bottom < 0 || rect.top > viewportHeight) {
        scrollProgress = 0;
      }
      
      // Avoid unnecessary updates if progress hasn't changed significantly
      if (Math.abs(scrollProgress - this.lastScrollProgress) < 0.001) {
        return;
      }
      this.lastScrollProgress = scrollProgress;
      
      // Update image scales and opacity
      this.imageWrappers.forEach((wrapper, index) => {
        const targetScale = this.scaleTargets[index] || 4;
        const currentScale = 1 + (targetScale - 1) * scrollProgress;
        
        // Fade out images from 0% to 70% scroll
        const imageOpacity = scrollProgress < 0.7 
          ? 1 - (scrollProgress / 0.7)
          : 0;
        
        // Round values to reduce paint operations
        const roundedScale = Math.round(currentScale * 100) / 100;
        const roundedOpacity = Math.round(imageOpacity * 100) / 100;
        
        // Apply with GPU acceleration
        wrapper.style.transform = `scale(${roundedScale}) translate3d(0, 0, 0)`;
        wrapper.style.opacity = roundedOpacity;
        
        // Add data attribute for CSS hooks
        if (roundedScale > 1.5) {
          wrapper.setAttribute('data-scaled', 'true');
        } else {
          wrapper.removeAttribute('data-scaled');
        }
      });
      
      // Update overlay opacity and scale
      if (this.overlay) {
        // Fade in text from 60% to 80%
        let textOpacity = 0;
        if (scrollProgress >= 0.6 && scrollProgress <= 0.8) {
          textOpacity = (scrollProgress - 0.6) / 0.2;
        } else if (scrollProgress > 0.8) {
          textOpacity = 1;
        }
        
        // Scale text from 0.8 to 1
        const textScale = 0.8 + (0.2 * textOpacity);
        
        // Round values
        const roundedTextScale = Math.round(textScale * 100) / 100;
        const roundedTextOpacity = Math.round(textOpacity * 100) / 100;
        
        this.overlay.style.transform = `scale(${roundedTextScale}) translate3d(0, 0, 0)`;
        this.overlay.style.opacity = roundedTextOpacity;
      }
    }

    debounce(func, wait) {
      let timeout;
      return function executedFunction(...args) {
        const later = () => {
          clearTimeout(timeout);
          func(...args);
        };
        clearTimeout(timeout);
        timeout = setTimeout(later, wait);
      };
    }

    destroy() {
      // Clean up event listeners
      window.removeEventListener('scroll', this.handleScroll);
      window.removeEventListener('resize', this.handleResize);
      
      // Clean up observers
      if (this.observer) {
        this.observer.disconnect();
      }
      if (this.imageObserver) {
        this.imageObserver.disconnect();
      }
      
      // Clean up will-change
      this.imageWrappers.forEach(wrapper => {
        wrapper.style.willChange = 'auto';
      });
      
      if (this.overlay) {
        this.overlay.style.willChange = 'auto';
      }
    }
  }

  let galleries = [];

  function initParallaxGallery() {
    // Clean up existing galleries
    galleries.forEach(gallery => gallery.destroy());
    galleries = [];

    const sections = document.querySelectorAll('.salonid-parallax');
    
    if (sections.length === 0) {
      return;
    }
    
    sections.forEach(section => {
      const gallery = new ParallaxGallery(section);
      galleries.push(gallery);
    });
    
    console.log('✅ SalonID Parallax Gallery initialized -', galleries.length, 'galleries');
  }

  // Initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initParallaxGallery);
  } else {
    initParallaxGallery();
  }

  // Re-initialize on Shopify theme editor updates
  if (window.Shopify && window.Shopify.designMode) {
    document.addEventListener('shopify:section:load', () => {
      setTimeout(initParallaxGallery, 100);
    });
    document.addEventListener('shopify:section:reorder', () => {
      setTimeout(initParallaxGallery, 100);
    });
  }

  // Cleanup on unload
  window.addEventListener('beforeunload', () => {
    galleries.forEach(gallery => gallery.destroy());
  });
})();